import { convertUserToContact } from '@debbie/common/dist/cases/index.js';
import { getContactPerson } from '@debbie/common/dist/customers/index.js';
import { fulfilRequirements, } from '@debbie/common/dist/workflows/index.js';
import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getBezierEdgeCenter, getBezierPath, useViewport, } from 'react-flow-renderer';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../theme.js';
import Badge from '../Badge.js';
import AlertTriangle from '../Icons/AlertTriangle.js';
import UserCheck from '../Icons/UserCheck.js';
import { EDGE_LABEL_HEIGHT, EDGE_LABEL_WIDTH } from './constants.js';
import RequirementsInput from './RequirementsInput.js';
const labelHeight = EDGE_LABEL_HEIGHT;
const labelWidth = EDGE_LABEL_WIDTH;
const labelGap = 8;
const labelStep = labelHeight + labelGap;
const tooltipWidth = 280;
export const EDGE_LABELS_OVERLAY_ID = 'rf-edge-labels-overlay';
const onEdgeClick = (evt, id) => { };
export default function CustomEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style = {}, markerEnd, data, }) {
    const lang = useSelector((s) => s.lang);
    const labelRef = useRef(null);
    const { x: panX, y: panY, zoom } = useViewport();
    const [isHovered, setIsHovered] = useState(false);
    const [tooltipPos, setTooltipPos] = useState(null);
    const edgePath = getBezierPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
    });
    const [edgeCenterX, edgeCenterY] = getBezierEdgeCenter({
        sourceX,
        sourceY,
        targetX,
        targetY,
    });
    const { timing, requirements, priority, manual, caseContext, warnings, siblingIndex, siblingCount, } = data;
    const timespan = (timing && timing.details && timing.details.timespan) || [];
    const labelOffsetY = siblingCount && siblingCount > 1
        ? (siblingIndex - (siblingCount - 1) / 2) * labelStep
        : 0;
    const isPossible = caseContext &&
        fulfilRequirements(requirements, {
            contact: convertUserToContact(getContactPerson(caseContext.customer, caseContext.singleCase)),
            updates: caseContext.updates,
            isCompany: caseContext.singleCase.isCompany,
            caseStatus: caseContext.singleCase.status,
            caseGroupId: caseContext.singleCase.caseGroup?.id || null,
            caseId: caseContext.singleCase.caseId,
            endReasonId: caseContext.singleCase.endReasonId,
            caseVouchers: caseContext.caseVouchers,
            creditorId: caseContext.singleCase.creditorId,
            hasFoundation: caseContext.hasFoundation,
            foundationTypes: caseContext.foundationTypes,
            customerTags: caseContext.customer.tags,
            caseTags: caseContext.singleCase.tags,
            interactions: caseContext.interactions,
            properties: caseContext.properties,
            debtRegistrations: caseContext.debtRegistrations,
        });
    const hasRequirements = (requirements?.length ?? 0) > 0;
    const onPathMouseEnter = () => {
        setIsHovered(true);
    };
    const onPathMouseLeave = () => {
        setIsHovered(false);
    };
    const onLabelMouseEnter = () => {
        setIsHovered(true);
        if (!labelRef.current) {
            return;
        }
        const rect = labelRef.current.getBoundingClientRect();
        const placement = rect.top > window.innerHeight / 2 ? 'above' : 'below';
        let left = rect.left + rect.width / 2 - tooltipWidth / 2;
        left = Math.max(8, Math.min(left, window.innerWidth - tooltipWidth - 8));
        const top = placement === 'above' ? rect.top - 8 : rect.top + rect.height + 8;
        setTooltipPos({ top, left, placement });
    };
    const onLabelMouseLeave = () => {
        setIsHovered(false);
        setTooltipPos(null);
    };
    const pathStyle = {
        ...style,
        transition: 'stroke 120ms ease',
        ...(isHovered && { stroke: THEME.COLOR_ACCENT }),
    };
    const overlayEl = typeof document !== 'undefined'
        ? document.getElementById(EDGE_LABELS_OVERLAY_ID)
        : null;
    const labelScreenX = panX + edgeCenterX * zoom;
    const labelScreenY = panY + (edgeCenterY + labelOffsetY) * zoom;
    return (React.createElement(React.Fragment, null,
        React.createElement("path", { id: id, style: pathStyle, className: "react-flow__edge-path", d: edgePath, markerEnd: markerEnd }),
        React.createElement("path", { d: edgePath, fill: "none", stroke: "transparent", strokeWidth: 16, style: { cursor: 'pointer' }, onMouseEnter: onPathMouseEnter, onMouseLeave: onPathMouseLeave }),
        overlayEl &&
            createPortal(React.createElement("div", { ref: labelRef, onMouseEnter: onLabelMouseEnter, onMouseLeave: onLabelMouseLeave, onClick: (event) => onEdgeClick(event, id), style: {
                    position: 'absolute',
                    left: labelScreenX,
                    top: labelScreenY,
                    transform: `translate(-50%, -50%) scale(${zoom})`,
                    transformOrigin: 'center',
                    pointerEvents: 'auto',
                    background: THEME.COLOR_SECONDARY,
                    border: `2px solid ${THEME.COLOR_ACCENT}`,
                    borderRadius: '3px',
                    padding: '3px 6px',
                    cursor: 'pointer',
                    fontSize: '10px',
                    lineHeight: 1,
                    userSelect: 'none',
                    boxSizing: 'border-box',
                    width: labelWidth,
                    height: labelHeight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '5px',
                    opacity: !caseContext || isPossible ? 1 : 0.5,
                } },
                React.createElement("span", { style: {
                        backgroundColor: THEME.COLOR_ACCENT,
                        padding: '0.4em',
                        borderRadius: '2px',
                        fontWeight: 'bold',
                        fontSize: '0.7em',
                        color: THEME.COLOR_SECONDARY,
                        flex: '0 0 auto',
                    } }, priority),
                manual ? (React.createElement(UserCheck, { style: {
                        height: '13px',
                        width: '13px',
                    } })) : (React.createElement("span", null,
                    React.createElement("b", null, timespan[0]),
                    ' ',
                    // @ts-ignore
                    lang.timespanUnits[timespan[1]])),
                !!warnings?.length && React.createElement(Badge, { theme: THEME.WARNING }, "!")), overlayEl),
        tooltipPos &&
            createPortal(React.createElement(TooltipBox, { style: {
                    top: tooltipPos.top,
                    left: tooltipPos.left,
                    width: tooltipWidth,
                    transform: tooltipPos.placement === 'above'
                        ? 'translateY(-100%)'
                        : undefined,
                } },
                !!warnings?.length &&
                    warnings.map((warning) => (React.createElement(TooltipWarning, { key: warning },
                        React.createElement(AlertTriangle, { style: {
                                width: '13px',
                                height: '13px',
                                margin: '0.15em 0 -0.15em',
                                flex: '0 0 auto',
                            } }),
                        ' ',
                        lang.workflowPathWarnings[warning]))),
                React.createElement(TooltipHeader, null, lang.workflows.requirements),
                hasRequirements ? (React.createElement(RequirementsInput, { disabled: true, requirements: requirements, onChange: () => { } })) : (React.createElement(EmptyState, null, lang.workflows.noRequirements))), document.body)));
}
const TooltipBox = styled.div `
  position: fixed;
  z-index: 1000;
  background: ${THEME.COLOR_SECONDARY};
  border: 1px solid #d9dce4;
  border-radius: 6px;
  padding: 10px 12px;
  box-shadow: 0 8px 24px rgba(15, 17, 33, 0.12);
  pointer-events: none;
  color: ${THEME.COLOR_PRIMARY};
`;
const TooltipWarning = styled.div `
  font-size: 12px;
  font-weight: 600;
  color: ${THEME.WARNING};
  margin-bottom: 6px;
`;
const TooltipHeader = styled.div `
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${THEME.COLOR_GREY};
  margin-bottom: 6px;
`;
const EmptyState = styled.div `
  font-size: 12px;
  color: ${THEME.COLOR_GREY};
  font-style: italic;
`;
//# sourceMappingURL=CustomEdge.js.map